/** API contract types for the self-service portal user-management proxy. */

export type PortalUserAccessRing = 'all' | 'beta' | 'alpha';

export type PortalUserSubscriptionTier =
  | 'free'
  | 'basic'
  | 'pro'
  | 'media_partner'
  | 'internal';

export type PortalUserStatus = 'active' | 'blocked';

export interface PortalSubscriptionUsage {
  gen_edit_used: number;
  gen_edit_limit: number | null;
  resize_used: number;
  resize_limit: number | null;
  /** Present on workspace payloads from Flow (credit ledger). */
  credit_balance?: number;
  granted_credit_balance?: number;
  purchased_credit_balance?: number;
  unlimited?: boolean;
}

export interface PortalUser {
  id: string;
  email: string;
  name: string;
  workspace_id: string | null;
  /** May be omitted by some upstream detail payloads; UI must tolerate absence. */
  subscription?: PortalUserSubscriptionTier;
  /** Early-access ring for in-development features; unrelated to billing tier. */
  access_ring?: PortalUserAccessRing;
  is_guest: boolean;
  is_blocked: boolean;
  status: PortalUserStatus;
  /** May be omitted by some upstream list payloads; UI treats absence as unverified. */
  email_verified?: boolean;
  email_verified_at?: string | null;
  monthly_used: number;
  monthly_limit: number;
  usage?: PortalSubscriptionUsage;
  creative_quota_override: number | null;
  /** Present when Flow returns credit-ledger fields (billing conversion path). */
  credit_balance?: number;
  granted_credit_balance?: number;
  purchased_credit_balance?: number;
  unlimited?: boolean;
  disable_billing_conversion: boolean;
  hide_conversion_modal: boolean;
  pending_campaigns: number;
  created_at: string;
  last_active_at?: string | null;
}

export interface PortalUsersPaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PortalUser[];
}

export interface PortalMetrics {
  new_users_30d: number;
  new_users_prior_30d: number;
  creatives_generated_30d: number;
  brands_ingested_30d: number;
  guest_signups_30d: number;
  guest_accounts_total: number;
  guest_accounts_unclaimed: number;
}

export interface PortalMetricsViewModel {
  newUsersLast30d: number;
  newUsersDelta: string;
  newUsersDeltaDirection: 'up' | 'down' | 'neutral';
  creativesGeneratedLast30d: number;
  brandsIngestedLast30d: number;
  guestSignupsLast30d: number;
  guestAccountsTotal: number;
  guestAccountsUnclaimed: number;
}

export type StatusFilter = 'all' | PortalUserStatus;
export type SubscriptionFilter = 'all' | PortalUserSubscriptionTier;
export type AccessRingFilter = 'all' | PortalUserAccessRing;

export interface ListPortalUsersParams {
  search?: string;
  status?: PortalUserStatus;
  subscription?: PortalUserSubscriptionTier;
  access_ring?: PortalUserAccessRing;
  is_guest?: boolean;
  page?: number;
  page_size?: number;
}

export interface AdminSubscription {
  workspace_id: string;
  workspace_name: string | null;
  tier: PortalUserSubscriptionTier;
  billing_cycle: 'none' | 'monthly' | 'annual';
  is_portal_managed: boolean;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  topup_credit_balance: number;
  usage: PortalSubscriptionUsage;
  tier_options: string[];
}

export interface AdminSubscriptionListResponse {
  results: AdminSubscription[];
  page: number;
  page_size: number;
}

export interface ListAdminSubscriptionsParams {
  tier?: PortalUserSubscriptionTier;
  page?: number;
  page_size?: number;
}

export interface PortalActivityLogEntry {
  id: string;
  action: string;
  actor_email: string | null;
  created_at: string;
  summary: string | null;
}

export interface PortalActivityLogResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PortalActivityLogEntry[];
}

export interface PortalUserDetail extends PortalUser {
  workspace_name?: string | null;
  activity_log: PortalActivityLogResponse;
}

export interface PortalWorkspaceSummary {
  workspace_id: string;
  workspace_name: string;
  member_count: number;
  active_member_count?: number;
  blocked_member_count?: number;
  tier: PortalUserSubscriptionTier;
  monthly_used: number;
  monthly_limit: number | null;
  usage?: PortalSubscriptionUsage;
  credit_balance?: number;
  unlimited?: boolean;
  pending_campaigns: number;
}

export interface PortalWorkspacesPaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PortalWorkspaceSummary[];
}

export interface PortalWorkspaceMember {
  id: string;
  email: string;
  name: string;
  status: PortalUserStatus;
  subscription: PortalUserSubscriptionTier;
  monthly_used: number;
  monthly_limit: number;
  is_guest: boolean;
}

export interface PortalWorkspaceDetail {
  workspace_id: string;
  workspace_name: string;
  tier: PortalUserSubscriptionTier;
  member_count: number;
  monthly_used: number;
  monthly_limit: number | null;
  credit_balance?: number;
  granted_credit_balance?: number;
  purchased_credit_balance?: number;
  unlimited?: boolean;
  pending_campaigns: number;
  usage: PortalSubscriptionUsage;
  members: PortalWorkspaceMember[];
}

export interface PortalWorkspaceBulkSettings {
  creative_quota_override: number | null;
  disable_billing_conversion: boolean;
  hide_conversion_modal: boolean;
}

export interface ListPortalWorkspacesParams {
  search?: string;
  subscription?: PortalUserSubscriptionTier;
  page?: number;
  page_size?: number;
}

export interface GetPortalUserDetailParams {
  userId: string;
  page?: number;
  page_size?: number;
}

export interface UpdatePortalWorkspaceBulkSettingsParams {
  workspaceId: string;
  creative_quota_override?: number | null;
  disable_billing_conversion?: boolean;
  hide_conversion_modal?: boolean;
}

export const PORTAL_USER_ACCESS_RINGS = [
  'all',
  'beta',
  'alpha',
] as const satisfies readonly PortalUserAccessRing[];

export const PORTAL_SYNC_NOT_CONFIGURED_ERROR_CODE = 'portal_sync_not_configured';

/** Tiers admins may set manually via the manage UI (workspace-scoped). */
export const ADMIN_SETTABLE_SUBSCRIPTION_TIERS = [
  'free',
  'basic',
  'pro',
  'media_partner',
  'internal',
] as const satisfies readonly PortalUserSubscriptionTier[];

export type AdminSettableSubscriptionTier = (typeof ADMIN_SETTABLE_SUBSCRIPTION_TIERS)[number];
